// src/utils/imageResize.ts
//
// הקטנת תמונה בדפדפן לפני ההעלאה.
//
// למה בצד הלקוח ולא בשרת: דיסק השורש בפרודקשן הוא 6.8G ומילוי שלו משבית
// את MySQL (קרה ביולי 2026). צילום ישיר מטלפון שוקל 4–8MB, ומאתיים כאלה
// מספיקים כדי להחזיר את התקלה. הקטנה בדפדפן פותרת את זה בלי להוסיף
// תלות נייטיבית בשרת (sharp) ובלי עומס CPU עליו.
//
// תוצאה טיפוסית: 6MB מהטלפון → 150–300KB.

const MAX_DIMENSION = 1600;
const QUALITY = 0.82;

export type ResizeResult = {
  file: File;
  originalSize: number;
  finalSize: number;
  width: number;
  height: number;
};

// בודק פעם אחת אם הדפדפן יודע לקודד WebP.
// ספארי יודע להציג WebP מזמן, אבל לקודד רק מ-16.4 — ולכן צריך בדיקה אמיתית.
let webpSupport: boolean | null = null;
const supportsWebp = (): boolean => {
  if (webpSupport !== null) return webpSupport;
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    webpSupport = canvas.toDataURL("image/webp").startsWith("data:image/webp");
  } catch {
    webpSupport = false;
  }
  return webpSupport;
};

const replaceExtension = (name: string, extension: string): string => {
  const base = name.replace(/\.[^.]+$/, "") || "image";
  // שם הקובץ הסופי נקבע בשרת; זה רק כדי שה-FormData יישלח עם סיומת עקבית
  return `${base}.${extension}`;
};

// createImageBitmap עם imageOrientation מכבד את נתוני ה-EXIF, כך שתמונה
// שצולמה במאונך לא תגיע מסובבת. יש דפדפנים שלא תומכים באפשרות הזו,
// ולכן יש נפילה חזרה ל-<img> רגיל.
const loadBitmap = async (file: File): Promise<ImageBitmap | HTMLImageElement> => {
  if (typeof createImageBitmap === "function") {
    try {
      // "from-image" נוסף ל-lib.dom אחרי גרסת ה-TypeScript שבשימוש כאן,
      // ולכן cast. בדפדפן זה נתמך; אם לא — ה-catch מטפל.
      return await createImageBitmap(file, {
        imageOrientation: "from-image" as any,
      } as ImageBitmapOptions);
    } catch {
      // ממשיכים לנפילה חזרה
    }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("לא ניתן לקרוא את קובץ התמונה"));
    };
    img.src = url;
  });
};

export const resizeImageForUpload = async (file: File): Promise<ResizeResult> => {
  // GIF מונפש היה מאבד את ההנפשה בקנבס — מעבירים כמו שהוא
  if (file.type === "image/gif") {
    return {
      file,
      originalSize: file.size,
      finalSize: file.size,
      width: 0,
      height: 0,
    };
  }

  const source = await loadBitmap(file);
  const sourceWidth = source.width;
  const sourceHeight = source.height;

  const scale = Math.min(1, MAX_DIMENSION / Math.max(sourceWidth, sourceHeight));
  const width = Math.round(sourceWidth * scale);
  const height = Math.round(sourceHeight * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("הדפדפן לא תומך בעיבוד תמונה");
  ctx.drawImage(source as CanvasImageSource, 0, 0, width, height);
  if ("close" in source) source.close();

  // WebP שומר שקיפות ומוריד משקל. בלעדיו: PNG נשאר PNG כדי לא לאבד
  // שקיפות, וכל השאר עובר ל-JPEG.
  let mimeType = "image/webp";
  let extension = "webp";
  if (!supportsWebp()) {
    if (file.type === "image/png") {
      mimeType = "image/png";
      extension = "png";
    } else {
      mimeType = "image/jpeg";
      extension = "jpg";
    }
  }

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, mimeType, QUALITY)
  );
  if (!blob) throw new Error("ההמרה של התמונה נכשלה");

  // אם ההמרה דווקא הגדילה את הקובץ (קורה בתמונות קטנות שכבר דחוסות),
  // עדיף לשלוח את המקור
  if (blob.size >= file.size && scale === 1) {
    return {
      file,
      originalSize: file.size,
      finalSize: file.size,
      width: sourceWidth,
      height: sourceHeight,
    };
  }

  return {
    file: new File([blob], replaceExtension(file.name, extension), { type: mimeType }),
    originalSize: file.size,
    finalSize: blob.size,
    width,
    height,
  };
};

export const formatFileSize = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
